import React, { useCallback } from 'react'
import classnames from 'classnames/bind'
import * as graph from '@adc/sdk'
import { bidding } from '@adc/blueprint'

import type {
  ClosingMethod,
  Listing,
  OnlineAuctionSegment,
  OnlineAuctionSegmentBidRank,
  CurrentTargetAudience,
} from '@adc/sdk/types'
import styles from './styles.scss'
import { acceptedOffer, getInitials } from '../utils'
import BidRankSummary from '../../bidRankSummary'
import WhatChangedSummary from '../../whatChangedSummary'
import GeneralModal from '../../../../overlays/generalModal'
// @ts-ignore no types
import {
  Button,
  Money,
  Text,
  LabelValue,
  VerticalExpander,
} from '../../../../elementals'
import { SlottableComponent } from '../../../../../types'
import BidRankDistancePill from '../bidRankDistancePill'

const cx = classnames.bind(styles)

interface BidRankDetailsProps extends SlottableComponent<'actions'> {
  bidRank: OnlineAuctionSegmentBidRank
  viewingFrom?: CurrentTargetAudience
  listing?: Listing
  segment?: OnlineAuctionSegment
  closingMethod?: ClosingMethod
  embeddedContext?: string
  featureFlags?: Record<string, boolean>
}
const BidRankDetails = ({
  bidRank,
  slots,
  listing,
  viewingFrom = graph.enums.CurrentTargetAudience.Seller,
  segment,
  closingMethod,
  embeddedContext,
  featureFlags = {},
}: BidRankDetailsProps) => {
  const [showWhatChanged, setShowWhatChanged] = React.useState(false)
  const name = `${
    bidRank?.party?.person
      ? getInitials(bidRank?.party?.person)
      : bidRank.party_id
  }'s`

  const isBuyer = viewingFrom === graph.enums.CurrentTargetAudience.Buyer
  const previousValues =
    bidRank?.bid && bidding.utils.getWhatFieldChanged(bidRank?.bid)
  const hasBidChanged =
    !!previousValues && featureFlags['fe.resi.enabled-bid-changed-summary']

  const handleWhatChangedModal = useCallback(() => {
    setShowWhatChanged(!showWhatChanged)
  }, [showWhatChanged])

  return (
    <div className={cx('bid-rank-details-container')}>
      <div
        data-elm-id='bid-rank-details-header'
        className={cx('bid-rank-details-header')}
      >
        <Text
          elmId='bid-rank-details-header-label'
          color='gray'
          size='big'
          weight='heavy'
        >
          {bidRank.belongs_to_current_user ? 'My' : name} Offer
        </Text>
        <Text
          elmId='bid-rank-details-header-sub-label'
          color='grayMedium'
          size='small'
          weight='normal'
        >
          Take a look at {bidRank.belongs_to_current_user ? 'your' : name} offer
          in detail.
        </Text>
      </div>
      <div className={cx('flex-container')}>
        <div className={cx('bid-rank-details-price-header')}>
          <div className={cx('bid-rank-details-price-amount')}>
            <LabelValue
              value={
                (isBuyer
                  ? bidRank?.bid?.terms?.offer_amount
                  : bidRank?.bid?.terms?.seller_net_offer_amount) ?? 0
              }
              label={isBuyer ? 'Purchase Price' : 'Net Offer Amount'}
              level={1}
              classNames={{
                label: cx('bid-rank-details-price-amount-label'),
                value: cx('bid-rank-details-price-amount-label'),
              }}
              isMoneyValue
              elmId='bid-rank-details-header-price'
            />
          </div>
          {hasBidChanged && (
            <Button
              size='large'
              role='secondary'
              onClick={handleWhatChangedModal}
              elmId='what_changed_button'
            >
              {isBuyer ? 'Previous Offer' : 'See What Changed'}
            </Button>
          )}
        </div>
        {bidRank.rank > 1 && !acceptedOffer(bidRank) ? (
          <div className={cx('from-first-place-container')}>
            <BidRankDistancePill bidRank={bidRank} isBuyer={isBuyer} />
          </div>
        ) : (
          ''
        )}
      </div>
      <BidRankSummary
        bidRank={bidRank}
        viewingFrom={viewingFrom}
        listing={listing}
        segment={segment}
        closingMethod={closingMethod}
        embeddedContext={embeddedContext}
      />
      {slots?.actions && (
        <div className={cx('bid-rank-details-footer')}>
          {slots?.actions?.({ bidRank })}
        </div>
      )}
      {hasBidChanged && (
        <GeneralModal
          size='tiny'
          open={showWhatChanged}
          mobileTitleSkin='light'
          elmId='what_changed_offer_elm_id'
          onClose={handleWhatChangedModal}
          actions={[
            {
              label: 'Close',
              role: 'primary',
              style: {
                marginLeft: '.75em',
              },
            },
          ]}
          title={
            <div
              data-elm-id='bid-rank-details-header'
              className={cx('bid-rank-details-header')}
            >
              <Text
                elmId='bid-rank-details-header-label'
                color='gray'
                size='big'
                weight='heavy'
              >
                {bidRank.belongs_to_current_user ? 'Your Updated' : name} Offer
              </Text>
              <Money
                elmId='bid-rank-details-header-label'
                color='gray'
                size='massive'
                weight='heavy'
                amount={
                  (isBuyer
                    ? bidRank?.bid?.terms?.offer_amount
                    : bidRank?.bid?.terms?.seller_net_offer_amount) ?? 0
                }
              />
              <Text
                elmId='bid-rank-details-header-sub-label'
                color='grayMedium'
                size='small'
                weight='normal'
              >
                Take a look at{' '}
                {bidRank.belongs_to_current_user ? 'the new' : name} offer in
                detail.
              </Text>
            </div>
          }
        >
          <VerticalExpander>
            <WhatChangedSummary
              bidRank={bidRank}
              viewingFrom={viewingFrom}
              listing={listing}
              segment={segment}
              previousValues={previousValues}
            />
          </VerticalExpander>
        </GeneralModal>
      )}
    </div>
  )
}

export default BidRankDetails
